import React, { memo, useEffect, useMemo, useState } from "react"
import styles from "./duplicateQueue.module.css"
import Select from "../../components/Form/Select"
import Button from "../../components/Form/Button"
import DatePicker from "../../components/Form/DatePicker"
import FormGroupInline from "../../components/Form/FormGroupInline"
import InfoTooltip from "../../components/InfoTooltip"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import { toast } from "react-toastify"
import { useAuth } from "../../contexts/authContext"
import { useData } from "../../contexts/dataContext"
import { useCrm } from "../../contexts/crmContext"
import { getStages } from "../../schemas/Stage"
import moment from "moment"
import { MdClose, MdFileDownload, MdFilterList, MdHistory, MdOpenInNew, MdOutlineFactCheck, MdPlaylistAddCheck, MdRefresh, MdViewList } from "react-icons/md"
import DuplicateReviewModal from "./DuplicateReviewModal"
import { getUsersBySelectedTeams, getValidUserIdsByTeams } from "../../utils/utils"

const typeOptions = [
    { id: "phone", value: "Telefone" },
    { id: "email", value: "E-mail" },
    { id: "document", value: "Documento" },
]

const priorityOptions = [
    { id: "high", value: "Alta" },
    { id: "medium", value: "Média" },
    { id: "low", value: "Baixa" },
]

const auditTypeOptions = [...typeOptions, { id: "name", value: "Nome" }]

const actionOptions = [
    { id: "merged", value: "Mescladas" },
    { id: "kept_separate", value: "Mantidas separadas" },
    { id: "ignored", value: "Ignoradas" },
]

const reasonOptions = [
    { id: "same_person_or_company", value: "Mesmo lead / mesma empresa" },
    { id: "same_person", value: "Mesmo lead" },
    { id: "same_company", value: "Mesma empresa" },
    { id: "shared_contact", value: "Contato compartilhado" },
    { id: "false_positive", value: "Não é duplicidade" },
    { id: "incomplete_data", value: "Dados insuficientes" },
    { id: "other", value: "Outro motivo" },
]

const scopeOptions = [
    { id: "selection", value: "Seleção atual" },
    { id: "group", value: "Grupo identificado" },
    { id: "identifier", value: "Identificador futuro" },
]

const summaryCards = [
    { id: "total", label: "Grupos pendentes", tone: "var(--brand-primary)" },
    { id: "high", label: "Alta prioridade", tone: "var(--status-danger)" },
    { id: "overdue", label: "SLA vencido", tone: "var(--status-danger)" },
    { id: "warning_sla", label: "Em atenção", tone: "var(--status-warning)" },
    { id: "oldest_pending_label", label: "Mais antigo", tone: "var(--brand-secondary)" },
    { id: "leads", label: "Leads envolvidos", tone: "var(--brand-secondary)" },
]

const auditSummaryCards = [
    { id: "total", label: "Decisões registradas", tone: "var(--brand-primary)" },
    { id: "merged", label: "Mesclagens", tone: "var(--status-success)" },
    { id: "kept_separate", label: "Mantidos separados", tone: "var(--status-warning)" },
    { id: "avg_resolution_label", label: "Tempo médio", tone: "var(--brand-secondary)" },
    { id: "resolved_over_72h", label: "Resolvidas após 72h", tone: "var(--status-danger)" },
]

const initialAuditFilters = {
    dateStart: moment().subtract(29, "days").format("YYYY-MM-DD"),
    dateEnd: moment().format("YYYY-MM-DD"),
    limit: 80,
}

const DuplicateQueueRow = memo(({ item, onReview }) => {
    const visibleLeads = (item.leads || []).slice(0, 4)
    const hiddenCount = Math.max(0, (item.leads || []).length - visibleLeads.length)

    return (
        <div className={styles.queueRow}>
            <div className={styles.identityCell}>
                <span className={`${styles.priorityBadge} ${styles[item.priority]}`}>{item.priority_label}</span>
                <div className={styles.identityCopy}>
                    <span className={styles.typeLabel}>{item.type_label}</span>
                    <strong>{item.value}</strong>
                </div>
            </div>
            <div className={styles.leadPreview}>
                {visibleLeads.map(lead => (
                    <span key={lead._id} className={styles.leadChip} title={lead.name}>{lead.name}</span>
                ))}
                {hiddenCount > 0 &&
                    <span className={`${styles.leadChip} ${styles.leadOverflow}`}>+{hiddenCount}</span>
                }
            </div>
            <div className={styles.queueStats}>
                <strong>{item.count} leads</strong>
                <span className={`${styles.slaBadge} ${styles[item?.sla?.status]}`}>Pendente há {item?.sla?.label || "-"}</span>
                <span className={styles.queueMeta}>Score {item.score || 0}</span>
            </div>
            <div className={styles.rowActions}>
                <Button variant="secondary" type="button" onClick={() => onReview(item)}>
                    <MdPlaylistAddCheck /> Revisar
                </Button>
            </div>
        </div>
    )
})

const formatStatusLabel = (status) => ({
    open: "Aberto",
    won: "Ganho",
    lost: "Perdido",
    archived: "Arquivado",
}[status] || status || "-")

const formatSlaLabel = (sla) => sla?.label || "Sem data"

const AuditLeadCard = ({ lead, label, onOpenLead }) => (
    <div className={styles.auditLeadCard}>
        <div>
            <span>{label}</span>
            <strong>{lead?.name || "-"}</strong>
        </div>
        <div className={styles.auditLeadMetaGrid}>
            <span>Status <strong>{formatStatusLabel(lead?.status)}</strong></span>
            <span>Funil <strong>{lead?.funnel_name || "-"}</strong></span>
            <span>Etapa <strong>{lead?.stage_name || "-"}</strong></span>
            <span>Equipe <strong>{lead?.team_name || "-"}</strong></span>
            <span>Responsável <strong>{lead?.owners?.map(owner => owner.name).join(", ") || "-"}</strong></span>
        </div>
        {lead?._id &&
            <Button variant="neutral" type="button" onClick={() => onOpenLead(lead._id)}>
                <MdOpenInNew /> Abrir lead
            </Button>
        }
    </div>
)

const AuditDecisionDetails = ({ item, onClose, onOpenLead }) => {
    if (!item) return null

    const affectedLeads = item.affected_leads || []

    return (
        <div className={styles.auditDetailsOverlay} role="dialog" aria-modal="true">
            <div className={styles.auditDetailsBackdrop} onClick={onClose} />
            <div className={styles.auditDetailsPanel}>
                <div className={styles.auditDetailsHeader}>
                    <div>
                        <span className={`${styles.auditActionBadge} ${styles[item.action]}`}>{item.action_label}</span>
                        <h3>Detalhe da decisão</h3>
                        <small>{item.duplicate_type_label} · {item.duplicate_value || "-"} · {moment(item.created_at).format("DD/MM/YYYY HH:mm")}</small>
                    </div>
                    <Button variant="neutral" type="button" onClick={onClose}>
                        <MdClose /> Fechar
                    </Button>
                </div>

                <div className={styles.auditDetailsSummary}>
                    <div>
                        <span>Motivo</span>
                        <strong>{item.reason_category_label || "-"}</strong>
                    </div>
                    <div>
                        <span>Escopo</span>
                        <strong>{item.decision_scope_label || "-"}</strong>
                    </div>
                    <div>
                        <span>Revisor</span>
                        <strong>{item.reviewed_by?.name || "Sem revisor"}</strong>
                    </div>
                    <div>
                        <span>Leads afetados</span>
                        <strong>{affectedLeads.length}</strong>
                    </div>
                    <div>
                        <span>Tempo até decisão</span>
                        <strong>{formatSlaLabel(item.resolution_sla)}</strong>
                    </div>
                </div>

                {item.reason &&
                    <div className={styles.auditDetailsNote}>
                        <span>Observação</span>
                        <p>{item.reason}</p>
                    </div>
                }

                <div className={styles.auditDetailsSection}>
                    <strong>Lead principal</strong>
                    <AuditLeadCard lead={item.primary_lead} label="Mantido como cadastro principal" onOpenLead={onOpenLead} />
                </div>

                <div className={styles.auditDetailsSection}>
                    <strong>Leads afetados pela decisão</strong>
                    <div className={styles.auditAffectedList}>
                        {affectedLeads.map(lead => (
                            <AuditLeadCard key={lead._id} lead={lead} label={lead._id === item.primary_lead?._id ? "Principal" : "Afetado"} onOpenLead={onOpenLead} />
                        ))}
                    </div>
                </div>

                <div className={styles.auditDecisionId}>
                    <MdOutlineFactCheck />
                    <span>ID da revisão: {item._id}</span>
                </div>
            </div>
        </div>
    )
}

const AuditList = ({ items = [], onOpenDetails }) => (
    <div className={styles.auditList}>
        {items.map(item => (
            <div className={styles.auditRow} key={item._id}>
                <div className={styles.auditDecision}>
                    <span className={`${styles.auditActionBadge} ${styles[item.action]}`}>{item.action_label}</span>
                    <div>
                        <strong>{item.duplicate_value || "-"}</strong>
                        <span>{item.duplicate_type_label} em {moment(item.created_at).format("DD/MM/YYYY HH:mm")}</span>
                        <small className={`${styles.slaBadge} ${styles[item?.resolution_sla?.status]}`}>Resolvida em {formatSlaLabel(item.resolution_sla)}</small>
                    </div>
                </div>
                <div className={styles.auditContext}>
                    <span>Principal</span>
                    <strong>{item.primary_lead?.name || "-"}</strong>
                </div>
                <div className={styles.auditContext}>
                    <span>Afetados</span>
                    <strong>{item.affected_leads?.length || 0} lead(s)</strong>
                </div>
                <div className={styles.auditContext}>
                    <span>Motivo</span>
                    <strong>{item.reason_category_label || "-"}</strong>
                </div>
                <div className={styles.auditContext}>
                    <span>Escopo</span>
                    <strong>{item.decision_scope_label || "-"}</strong>
                </div>
                <div className={styles.auditReviewer}>
                    <span>{item.reviewed_by?.name || "Sem revisor"}</span>
                    {item.reason && <small>{item.reason}</small>}
                </div>
                <div className={styles.auditRowActions}>
                    <Button variant="neutral" type="button" onClick={() => onOpenDetails(item)}>
                        <MdOutlineFactCheck /> Detalhes
                    </Button>
                </div>
            </div>
        ))}
    </div>
)

const DuplicateQueue = () => {
    const { teams, users, funnels, origins } = useData()
    const { permissions } = useAuth()
    const { openModalLead } = useCrm()
    const [activeView, setActiveView] = useState("queue")
    const [filters, setFilters] = useState({ limit: 100 })
    const [data, setData] = useState({ items: [], summary: {} })
    const [auditFilters, setAuditFilters] = useState(initialAuditFilters)
    const [auditData, setAuditData] = useState({ items: [], summary: {}, charts: {} })
    const [stages, setStages] = useState([])
    const [loading, setLoading] = useState(false)
    const [auditLoading, setAuditLoading] = useState(false)
    const [selectedCandidate, setSelectedCandidate] = useState(null)
    const [selectedAuditItem, setSelectedAuditItem] = useState(null)

    useEffect(() => {
        if (funnels?.length && !filters.funnels?.length) {
            handleChangeFilters("funnels", funnels.map(funnel => funnel.id))
        }
    }, [funnels])

    useEffect(() => {
        const loadStages = async () => {
            if (!filters?.funnels?.length) {
                setStages([])
                return
            }

            const stagesByFunnel = await Promise.all(filters.funnels.map(funnel_id => getStages(funnel_id)))
            const uniqueStages = new Map()
            stagesByFunnel.flat().forEach(stage => {
                if (stage?._id) uniqueStages.set(stage._id, { id: stage._id, value: stage.name })
            })
            setStages([...uniqueStages.values()])
        }

        loadStages()
    }, [filters.funnels])

    useEffect(() => {
        getDataByFilter()
    }, [])

    useEffect(() => {
        if (activeView === "audit" && !auditData.items.length && !auditLoading) {
            getAuditByFilter()
        }
    }, [activeView])

    const handleChangeFilters = (name, value) => {
        setFilters(previousValue => ({
            ...previousValue,
            [name]: value,
            ...(name === "teams" ? { users: getValidUserIdsByTeams(previousValue.users, value, users, teams, [], previousValue.teams) } : {}),
        }))
    }

    const handleChangeAuditFilters = (name, value) => {
        setAuditFilters(previousValue => ({ ...previousValue, [name]: value }))
    }

    const handleChangeAuditPeriod = (dateStart, dateEnd) => {
        setAuditFilters(previousValue => ({ ...previousValue, dateStart, dateEnd }))
    }

    const getDataByFilter = async () => {
        try {
            setLoading(true)
            const queryString = new URLSearchParams(filters).toString()
            const response = await api.get(`/leads/duplicates/queue?${queryString}`)
            setData(response.data?.data || { items: [], summary: {} })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar a fila de duplicidades.")
        } finally {
            setLoading(false)
        }
    }

    const getAuditByFilter = async () => {
        try {
            setAuditLoading(true)
            const queryString = new URLSearchParams(auditFilters).toString()
            const response = await api.get(`/leads/duplicates/reviews?${queryString}`)
            setAuditData(response.data?.data || { items: [], summary: {}, charts: {} })
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível carregar a auditoria de duplicidades.")
        } finally {
            setAuditLoading(false)
        }
    }

    const exportDuplicateReviews = async () => {
        try {
            setAuditLoading(true)
            const queryString = new URLSearchParams(auditFilters).toString()
            const response = await api.get(`/leads/duplicates/reviews/export?${queryString}`, { responseType: "blob" })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement("a")
            link.href = url
            link.download = `revisoes-duplicidade-${new Date().toISOString().replace(/[:.]/g, "-")}.xlsx`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível exportar as revisões de duplicidade.")
        } finally {
            setAuditLoading(false)
        }
    }

    const handleOpenLead = (lead_id) => {
        if (!lead_id) return
        openModalLead(lead_id)
    }

    const summary = data?.summary || {}
    const auditSummary = auditData?.summary || {}
    const items = useMemo(() => data?.items || [], [data])
    const auditItems = useMemo(() => auditData?.items || [], [auditData])
    const activeFilterCount = useMemo(() => Object.entries(filters).filter(([key, value]) => key !== "limit" && (Array.isArray(value) ? value.length : value)).length, [filters])
    const activeAuditFilterCount = useMemo(() => Object.entries(auditFilters).filter(([key, value]) => !["limit", "dateStart", "dateEnd"].includes(key) && (Array.isArray(value) ? value.length : value)).length, [auditFilters])

    return (
        <div className={styles.queuePage}>
            <div className={styles.queueHeader}>
                <div className={styles.queueTitle}>
                    <div className={styles.queueTitleIcon}>
                        <MdPlaylistAddCheck />
                    </div>
                    <div>
                        <h2>Fila de duplicidades</h2>
                        <span>Revise grupos de leads com identificadores repetidos e consolide a base com segurança.</span>
                    </div>
                </div>
                <div className={styles.queueControls}>
                    <Button variant={activeView === "queue" ? "secondary" : "neutral"} type="button" onClick={() => setActiveView("queue")}>
                        <MdViewList /> Fila
                    </Button>
                    <Button variant={activeView === "audit" ? "secondary" : "neutral"} type="button" onClick={() => setActiveView("audit")}>
                        <MdHistory /> Auditoria
                    </Button>
                    <Button variant="secondary" type="button" onClick={activeView === "queue" ? getDataByFilter : getAuditByFilter}>
                        <MdRefresh /> Atualizar
                    </Button>
                </div>
            </div>

            {activeView === "queue" &&
                <>
                    <div className={styles.filtersPanel}>
                        <div>
                            <div className={styles.filterGrid}>
                                <Select name="Funil" placeholder="Pesquise um funil" options={funnels || []} selected={filters.funnels || []} onChange={(value) => handleChangeFilters("funnels", value)} multiple={true} />
                                <Select name="Etapa" placeholder="Pesquise uma etapa" options={stages || []} selected={filters.stages || []} onChange={(value) => handleChangeFilters("stages", value)} multiple={true} />
                                <Select name="Equipe" placeholder="Pesquise uma equipe" options={teams || []} selected={filters.teams || []} onChange={(value) => handleChangeFilters("teams", value)} multiple={true} />
                                {permissions?.crm > 1 &&
                                    <Select name="Responsável" placeholder="Pesquise um responsável" options={getUsersBySelectedTeams(filters.teams, users, teams)} selected={filters.users || []} onChange={(value) => handleChangeFilters("users", value)} multiple={true} />
                                }
                                <Select name="Origem" placeholder="Pesquise uma origem" options={origins || []} selected={filters.origins || []} onChange={(value) => handleChangeFilters("origins", value)} multiple={true} />
                                <Select name="Tipo" placeholder="Pesquise um tipo" options={typeOptions} selected={filters.types || []} onChange={(value) => handleChangeFilters("types", value)} multiple={true} />
                                <Select name="Prioridade" placeholder="Pesquise uma prioridade" options={priorityOptions} selected={filters.priority || []} onChange={(value) => handleChangeFilters("priority", value)} multiple={true} />
                            </div>
                            <p className={styles.filterHint}>{activeFilterCount ? `${activeFilterCount} filtro(s) ativo(s)` : "Sem filtros adicionais aplicados"}</p>
                        </div>
                        <div>
                            <FormGroupInline style={{ justifyContent: "flex-end" }}>
                                <Button variant="secondary" type="button" onClick={getDataByFilter}><MdFilterList /> Aplicar</Button>
                                <Button variant="primary" type="button" onClick={() => setFilters({ limit: 100 })}>Limpar</Button>
                            </FormGroupInline>
                        </div>
                    </div>

                    <div className={styles.summaryGrid}>
                        {summaryCards.map(card => (
                            <div className={styles.summaryCard} style={{ "--card-tone": card.tone }} key={card.id}>
                                <span>{card.label}</span>
                                <strong>{summary[card.id] || 0}</strong>
                            </div>
                        ))}
                    </div>

                    <div className={styles.queueTable}>
                        <div className={styles.queueScroller}>
                            <div className={styles.queueTableHeader}>
                                <span>Identificador</span>
                                <span>Leads do grupo</span>
                                <span>Impacto e SLA</span>
                                <span>Ação</span>
                            </div>
                            {items.map(item => (
                                <DuplicateQueueRow key={`${item.type}-${item.value}`} item={item} onReview={setSelectedCandidate} />
                            ))}
                            {!loading && !items.length &&
                                <div className={styles.emptyQueue}>
                                    <strong>Nenhuma duplicidade pendente</strong>
                                    <span>Os filtros atuais não retornaram grupos para revisão.</span>
                                </div>
                            }
                        </div>
                    </div>
                </>
            }

            {activeView === "audit" &&
                <div className={styles.auditPage}>
                    <div className={styles.filtersPanel}>
                        <div>
                            <div className={`${styles.filterGrid} ${styles.auditFilterGrid}`}>
                                <DatePicker name="Período" dateStart={auditFilters.dateStart || ""} dateEnd={auditFilters.dateEnd || ""} onChange={handleChangeAuditPeriod} multiple={true} />
                                <Select name="Decisão" placeholder="Pesquise uma decisão" options={actionOptions} selected={auditFilters.actions || []} onChange={(value) => handleChangeAuditFilters("actions", value)} multiple={true} />
                                <Select name="Tipo" placeholder="Pesquise um tipo" options={auditTypeOptions} selected={auditFilters.types || []} onChange={(value) => handleChangeAuditFilters("types", value)} multiple={true} />
                                <Select name="Motivo" placeholder="Pesquise um motivo" options={reasonOptions} selected={auditFilters.reason_categories || []} onChange={(value) => handleChangeAuditFilters("reason_categories", value)} multiple={true} />
                                <Select name="Escopo" placeholder="Pesquise um escopo" options={scopeOptions} selected={auditFilters.scopes || []} onChange={(value) => handleChangeAuditFilters("scopes", value)} multiple={true} />
                            </div>
                            <p className={styles.filterHint}>
                                {activeAuditFilterCount ? `${activeAuditFilterCount} filtro(s) de auditoria ativo(s)` : "Período dos últimos 30 dias"}
                                <InfoTooltip text="A auditoria mostra as decisões tomadas na fila: quem revisou, qual ação foi escolhida, motivo, escopo e leads afetados." preferredPosition="bottom" />
                            </p>
                        </div>
                        <div>
                            <FormGroupInline style={{ justifyContent: "flex-end" }}>
                                <Button variant="secondary" type="button" onClick={getAuditByFilter}><MdFilterList /> Aplicar</Button>
                                <Button variant="neutral" type="button" onClick={exportDuplicateReviews}><MdFileDownload /> Exportar</Button>
                                <Button variant="primary" type="button" onClick={() => setAuditFilters(initialAuditFilters)}>Limpar</Button>
                            </FormGroupInline>
                        </div>
                    </div>

                    <div className={styles.summaryGrid}>
                        {auditSummaryCards.map(card => (
                            <div className={styles.summaryCard} style={{ "--card-tone": card.tone }} key={card.id}>
                                <span>{card.label}</span>
                                <strong>{auditSummary[card.id] || 0}</strong>
                            </div>
                        ))}
                    </div>

                    <div className={styles.auditInsights}>
                        {(auditData?.charts?.actions || []).map(item => (
                            <div className={styles.auditInsight} key={`action-${item.id}`}>
                                <span>{item.name}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                        {(auditData?.charts?.reasons || []).slice(0, 4).map(item => (
                            <div className={styles.auditInsight} key={`reason-${item.id}`}>
                                <span>{item.name}</span>
                                <strong>{item.value}</strong>
                            </div>
                        ))}
                    </div>

                    <div className={styles.auditPanel}>
                        <div className={styles.auditPanelHeader}>
                            <div>
                                <strong>Histórico de decisões</strong>
                                <span>Últimos registros encontrados para os filtros selecionados.</span>
                            </div>
                            <small>{auditItems.length} item(ns)</small>
                        </div>
                        <AuditList items={auditItems} onOpenDetails={setSelectedAuditItem} />
                        {!auditLoading && !auditItems.length &&
                            <div className={styles.emptyQueue}>
                                <strong>Nenhuma decisão registrada</strong>
                                <span>Não há revisões de duplicidade no período ou filtros escolhidos.</span>
                            </div>
                        }
                    </div>
                </div>
            }

            <DuplicateReviewModal candidate={selectedCandidate} onClose={() => setSelectedCandidate(null)} onResolved={getDataByFilter} />
            <AuditDecisionDetails item={selectedAuditItem} onClose={() => setSelectedAuditItem(null)} onOpenLead={handleOpenLead} />
            {(loading || auditLoading) && <Loading fullPage={true} />}
        </div>
    )
}

export default DuplicateQueue
